// Client feedback — NPS (ideas track, idea 41). Prepares (never sends) the survey message after a held meeting or a
// closed claim; records the answers the platform collected; 9–10 → Google-review draft, 0–6 → call-back task (domain).
import { useEffect, useMemo, useRef } from 'react';
import {
  prepareNpsSurvey, recordNpsAnswer, markNpsReviewAsked, withdrawNpsSurvey, npsSuggested, npsReviewUrl, npsLink, npsSummary, npsByMonth, surveyFor,
  addDays, saveSetting, NPS_REVIEW_URL_KEY, type NpsTrigger,
} from '@vero/domain';
import { useRuntime, type Runtime } from '../core/runtime.tsx';
import { Empty, Field } from '../core/ui.tsx';
import { Icon } from '../core/icons.tsx';
import { openMessage } from '../core/messages.tsx';
import { accountService } from '../core/account-service.ts';
import { useAccount } from './account.tsx';

/** the platform address the survey link points to (the signed-in account's server), or null when not signed in */
const platformBase = (): string | null => { const a = accountService(); return a?.snapshot().signedIn ? a.serverUrl ?? null : null; };

/** prepare a survey and open the message composer with the link (the advisor reads, edits and sends it) */
export async function prepareAndCompose(rt: Runtime, personId: string, trigger: NpsTrigger, ref?: { table: string; id: string }) {
  const base = platformBase();
  if (!base) { rt.toast(rt.t('nps.need_account'), 'error'); return; }
  const cs = await rt.run((v, c) => prepareNpsSurvey(v, { personId, trigger, refTable: ref?.table ?? null, refId: ref?.id ?? null, lang: rt.locale.startsWith('el') ? 'el' : 'en', advisorName: rt.brand.advisorName ?? rt.advisor.name ?? null }, c), 'nps.prepared');
  const s = cs?.entityId ? rt.view.get('nps_surveys', cs.entityId) ?? rt.ws.view.get('nps_surveys', cs.entityId) : undefined;
  if (s) openMessage(rt, personId, 'nps_invite', { nps: { link: npsLink(base, s.token) } });
}

/** a small prompt shown after a meeting is recorded or a claim is closed: «ask for feedback?» */
export function NpsPrompt({ personId, trigger, refTable, refId }: { personId: string; trigger: NpsTrigger; refTable: string; refId: string }) {
  const rt = useRuntime();
  const { t, view } = rt;
  const existing = surveyFor(view, refTable, refId);
  const ok = npsSuggested(view, personId, new Date().toISOString());
  if (existing && existing.status !== 'withdrawn') return <span className="chip ok" data-testid="nps-prepared">{t(existing.status === 'answered' ? 'nps.answered_chip' : 'nps.prepared_chip', { score: existing.score ?? '' })}</span>;
  if (!ok.ok) return null;
  return <button className="btn btn-sm btn-ghost" onClick={(e: { stopPropagation(): void }) => { e.stopPropagation(); void prepareAndCompose(rt, personId, trigger, { table: refTable, id: refId }); }} data-testid="nps-ask"><Icon name="star" size={14} />{t('nps.ask')}</button>;
}

/** Focus → Conversations: the client's feedback (answers, 9–10 review draft, 0–6 call-back) */
export function NpsPanel({ personId }: { personId: string }) {
  const rt = useRuntime();
  const { t, i18n, view } = rt;
  const rows = useMemo(() => [...view.where('nps_surveys', 'personId', personId)].sort((a, b) => b.preparedAt.localeCompare(a.preparedAt)), [view, personId]);
  const review = npsReviewUrl(view);
  const base = platformBase();
  return (
    <section className="card" data-testid="nps-panel">
      <div className="card-head"><div><h3>{t('nps.title')}</h3><div className="small muted">{t('nps.subtitle')}</div></div>
        <button className="btn btn-sm" onClick={() => void prepareAndCompose(rt, personId, 'manual')} data-testid="nps-manual"><Icon name="star" size={14} />{t('nps.ask')}</button></div>
      {rows.length ? <div className="agenda-list">{rows.map((s) => (
        <div key={s.id} className="agenda-item" style={{ gridTemplateColumns: 'minmax(0,1fr) auto' }} data-testid="nps-row">
          <div style={{ minWidth: 0 }}>
            <div className="t">{s.status === 'answered' ? <><b className="num">{s.score}/10</b> · {t(`nps.category.${s.category}`)}</> : t(`nps.status.${s.status}`)}</div>
            <div className="s">{t(`nps.trigger.${s.trigger}`)} · {i18n.date((s.answeredAt ?? s.preparedAt).slice(0, 10))}{s.comment ? ` · «${s.comment}»` : ''}</div>
          </div>
          <div className="row" style={{ gap: 6 }}>
            {s.status === 'prepared' && base && <button className="btn btn-sm btn-ghost" onClick={() => openMessage(rt, personId, 'nps_invite', { nps: { link: npsLink(base, s.token) } })}><Icon name="mail" size={14} />{t('nps.resend')}</button>}
            {s.status === 'prepared' && <button className="btn btn-sm btn-ghost" onClick={() => void rt.run((v, c) => withdrawNpsSurvey(v, s.id, c), 'nps.withdrawn')}>{t('nps.withdraw')}</button>}
            {s.category === 'promoter' && (review
              ? <button className="btn btn-sm btn-primary" onClick={() => { void rt.run((v, c) => markNpsReviewAsked(v, s.id, c)); openMessage(rt, personId, 'google_review', { review: { link: review } }); }} data-testid="nps-review">{t(s.reviewAskedAt ? 'nps.review_again' : 'nps.review')}</button>
              : <span className="tiny muted">{t('nps.review_no_link')}</span>)}
            {s.category === 'detractor' && s.taskId && <span className={`chip ${view.get('tasks', s.taskId)?.status === 'open' ? 'high' : 'ok'}`}>{t('nps.callback')}</span>}
          </div>
        </div>
      ))}</div> : <Empty icon="star">{t('nps.empty')}</Empty>}
    </section>
  );
}

/** while signed in: pull the answers the platform collected and record them on the client cards (every 10 minutes) */
export function NpsWatcher() {
  const rt = useRuntime();
  const a = useAccount();
  const busy = useRef(false);
  useEffect(() => {
    if (!a?.signedIn) return;
    const pull = async () => {
      const api = accountService()?.api;
      if (!api || busy.current || rt.ws.readOnlyReason) return;
      busy.current = true;
      try {
        const { answers } = await api.npsAnswers();
        for (const x of answers) {
          const s = rt.ws.view.where('nps_surveys', 'token', x.token)[0];
          if (!s || s.status === 'answered') continue;
          await rt.run((v, c) => recordNpsAnswer(v, x, c, (n) => rt.t('nps.callback_title', { score: n })));
        }
      } catch { /* offline or not on this platform version: next time */ } finally { busy.current = false; }
    };
    void pull();
    const id = setInterval(() => void pull(), 10 * 60_000);
    return () => clearInterval(id);
  }, [a?.signedIn]);
  return null;
}

/** Analytics → client feedback: NPS per month (bars) and the last 12 months' split */
export function NpsChart() {
  const rt = useRuntime();
  const { t, i18n, view, today } = rt;
  const months = useMemo(() => npsByMonth(view, today, 12), [view, today]);
  const year = useMemo(() => npsSummary(view, addDays(today, -365), today), [view, today]);
  const fmt = new Intl.DateTimeFormat(rt.locale, { month: 'short' });
  return (
    <section className="card card-pad" style={{ marginTop: 18 }} data-testid="nps-chart">
      <div className="section-title">{t('nps.chart_title')}</div>
      <div className="kv">
        <div><span>{t('nps.score_12m')}</span><b className="num">{year.nps == null ? '—' : i18n.number(year.nps)}</b></div>
        <div><span>{t('nps.answers')}</span><b>{year.answered}</b>{year.responseRate != null ? <span className="tiny muted"> · {t('nps.response_rate', { pct: year.responseRate })}</span> : null}</div>
        <div><span>{t('nps.category.promoter')}</span><b>{year.promoters}</b></div>
        <div><span>{t('nps.category.passive')}</span><b>{year.passives}</b></div>
        <div><span>{t('nps.category.detractor')}</span><b>{year.detractors}</b></div>
      </div>
      <div role="img" aria-label={t('nps.chart_alt', { list: months.map((m) => `${m.month}: ${m.nps ?? '—'}`).join(', ') })} style={{ display: 'grid', gridTemplateColumns: `repeat(${months.length}, 1fr)`, gap: 6, alignItems: 'end', height: 140, marginTop: 14 }}>
        {months.map((m) => (
          <div key={m.month} title={`${m.month}: ${m.nps == null ? t('nps.no_answers') : `NPS ${m.nps} · ${m.answered}`}`} style={{ display: 'grid', gridTemplateRows: '1fr auto', height: '100%', textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>{m.nps == null ? <span className="tiny muted">—</span> : <i style={{ display: 'block', width: '70%', height: `${Math.max(4, ((m.nps + 100) / 200) * 100)}%`, background: m.nps >= 0 ? 'var(--s1)' : 'var(--danger, #c0583c)', borderRadius: 4 }} />}</div>
            <span className="tiny muted">{fmt.format(new Date(`${m.month}-15T12:00:00Z`))}</span>
          </div>
        ))}
      </div>
      <p className="tiny muted" style={{ marginBottom: 0 }}>{t('nps.chart_note')}</p>
    </section>
  );
}

/** Settings: the Google review link used in the 9–10 draft */
export function NpsSettingsField() {
  const rt = useRuntime();
  const { t, view } = rt;
  return (
    <Field label={t('nps.review_url')} hint={t('nps.review_url_hint')} span2>
      <input className="input" type="url" defaultValue={npsReviewUrl(view) ?? ''} placeholder="https://g.page/r/…/review" onBlur={(e: { target: HTMLInputElement }) => {
        const v = e.target.value.trim();
        if (v && !/^https:\/\/\S+$/.test(v)) { rt.toast(t('nps.review_url_invalid'), 'error'); return; }
        void rt.run((vw, c) => saveSetting(vw, NPS_REVIEW_URL_KEY, v, c), 'settings.saved');
      }} data-testid="settings-review-url" />
    </Field>
  );
}
